import { BodyZonePartManager } from '@/screens/body-zones/BodyZoneManager';
import { Exercises } from '@/screens/exercises/Exercises';
import { ForgotPassword } from '@/screens/login/ForgotPassword';
import { MyAccount } from '@/screens/MyAccount';
import { Privacy } from '@/screens/Privacy';
import { Decks } from '@/screens/decks/Decks';
import { UserGuide } from '@/screens/UserGuide';
import { Users, TraineeAssignment } from '@/screens/users';
import { VerificationComplete } from '@/screens/VerificationComplete';
import { EquipmentManager } from '@/screens/equipment/EquipmentManager';
import { isAdminUser, isLoggedIn, isPremiumUser, isTrainerUser } from '@/utils/user';
import { Routes, Route, Navigate } from 'react-router-dom';
import { GoogleReCaptchaProvider } from '../utils/legacy-deps';
import { useContext, useEffect } from 'react';
import { Support } from '@/screens/Support';
import { Login } from '@/screens/login/Login';
import { Plans } from '@/screens/plans/Plans';
import { Plan } from '@/screens/plans/plan/Plan';
import { Trainees } from '@/screens/trainees/Trainees';
import { Register } from '@/screens/Register';
import { Maintenance } from '@/screens/Maintenance';
import { TrainingHistoryScreen } from '@/screens/training-history/TrainingHistoryScreen';
import { WorkoutUnit } from '@/screens/plans/workout-unit/WorkoutUnit';
import { Workout } from '@/screens/plans/workout/Workout';
import { TraineePlanAssignment } from '@/screens/trainees/TraineePlanAssignment';
import { SubscriptionSuccess } from '@/screens/subscription/SubscriptionSuccess';
import { AppContext } from '@/contexts/app.context';
import { pathMap } from './paths';
import { UpdateExercisePage } from '@/screens/exercises/UpdateExercisePage';
import { ExerciseCategories } from '@/screens/exercise-categories/ExerciseCategories';
import { Glossary } from '@/screens/glossary/Glossary';
import { ExerciseDetailsPage } from '@/screens/exercises/ExerciseDetailsPage';

export const RoutesSection = () => {
  const context = useContext(AppContext);

  return (
    <Routes>
      <Route path="/" element={!isLoggedIn() ? <Login redirectLocation={pathMap.Decks} /> : <Navigate to={pathMap.Decks}></Navigate>}></Route>
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/user/forgot-password" element={<ForgotPassword />} />
      <Route path="/user/verify/:result" element={<VerificationComplete />} />
      <Route path="/user-guide/:pageIndex?" element={<UserGuide />} />
      <Route path="/login/:id?" element={<Login redirectLocation={pathMap.Decks} />}></Route>
      <Route path="/qr-code" element={<QRCodeRedirect />}></Route>
      <Route path="/support" element={<Support />} />
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route
        path="/register"
        element={
          <GoogleReCaptchaProvider reCaptchaKey={'6LcP7k0kAAAAAKDs9U5qPzGOBxOJpm1F9sfnnET8'}>
            <Register />
          </GoogleReCaptchaProvider>
        }
      />
      {context.state?.userProfile && (
        <>
          <Route path="/equipment" element={isAdminUser(context.state?.userProfile) ? <EquipmentManager /> : <Navigate to="/equipment" />} />
          <Route path="/users" element={isAdminUser(context.state?.userProfile) ? <Users /> : <Navigate to={pathMap.Decks} />} />
          <Route path="/history/:userId?" element={!isLoggedIn() ? <Navigate to={`${pathMap.Decks}`} /> : <TrainingHistoryScreen />} />
          <Route path="/my-account" element={!isLoggedIn() ? <Login redirectLocation="/my-account" /> : <MyAccount />} />
          <Route path={`${pathMap.Decks}/:userId?`} element={!isLoggedIn() ? <Login redirectLocation={`${pathMap.Decks}`} /> : <Decks />} />
          <Route
            path={`${pathMap.ExerciseCategories}`}
            element={isLoggedIn() && isAdminUser(context.state?.userProfile) ? <ExerciseCategories /> : <Login redirectLocation={`${pathMap.ExerciseCategories}`} />}
          ></Route>
          <Route path={`${pathMap.Exercises}/:deckId?/*`} element={!isLoggedIn() ? <Login redirectLocation={`${pathMap.Exercises}`} /> : <Exercises />}></Route>
          <Route path={`${pathMap.EditExercise}/:exerciseId?`} element={!isLoggedIn() ? <Login redirectLocation={`${pathMap.Exercises}`} /> : <UpdateExercisePage />}></Route>
          <Route path={`${pathMap.ExerciseDetails}/:exerciseId?`} element={!isLoggedIn() ? <Login redirectLocation={`${pathMap.Exercises}`} /> : <ExerciseDetailsPage />}></Route>
          <Route path={`${pathMap.BodyZones}/*`} element={isLoggedIn() ? <BodyZonePartManager /> : <Navigate to="/body-zones" />} />
          <Route path={`${pathMap.Plans}`} element={isLoggedIn() ? <Plans /> : <Navigate to={`${pathMap.Decks}`} />} />
          <Route path={`${pathMap.Plan}/:planId?`} element={isLoggedIn() ? <Plan /> : <Navigate to={`${pathMap.Decks}`} />} />
          <Route path={`${pathMap.Plan}/:planId/:workoutUnitId?`} element={isLoggedIn() ? <WorkoutUnit /> : <Navigate to={`${pathMap.Decks}`} />} />
          <Route
            path={`${pathMap.Plan}/:planId/:workoutUnitId?/:workoutId?`}
            element={isPremiumUser(context.state?.userProfile) ? <Workout /> : <Navigate to={`${pathMap.Decks}`} />}
          />
          <Route
            path="/trainee-plan-assignment/:traineeId"
            element={isPremiumUser(context.state?.userProfile) ? <TraineePlanAssignment /> : <Navigate to={`${pathMap.Decks}`} />}
          />
          <Route path="/trainee-assignment" element={isAdminUser(context.state?.userProfile) ? <TraineeAssignment /> : <Navigate to={`${pathMap.Decks}`} />} />
          <Route path="/trainees" element={isTrainerUser(context.state?.userProfile) ? <Trainees /> : <Navigate to={`${pathMap.Decks}`} />} />
          <Route path="/maintenance" element={isAdminUser(context.state?.userProfile) ? <Maintenance /> : <Navigate to={`${pathMap.Decks}`} />} />
          <Route path={`${pathMap.Glossary}/:glossaryTermId?`} element={isAdminUser(context.state?.userProfile) ? <Glossary /> : <Navigate to={`${pathMap.Decks}`} />} />
        </>
      )}
    </Routes>
  );
};

const QRCodeRedirect = () => {
  const userAgent = navigator.userAgent.toLowerCase();

  useEffect(() => {
    if (/android/i.test(userAgent)) {
      window.location.href = process.env.REACT_APP_PLAY_STORE_URI ?? '';
    } else if (/iphone|ipad|ipod/i.test(userAgent)) {
      window.location.href = process.env.REACT_APP_APP_STORE_URI ?? '';
    } else {
      window.location.href = process.env.REACT_APP_NO_MOBILE_DEVICE_REDIRECT_URL ?? '';
    }
  }, []);

  return <></>;
};
